import React, { useMemo, useCallback } from "react";
import { useQuery } from "@apollo/client/react";
import Dialog from "@mui/material/Dialog";
import CircularProgress from "@mui/material/CircularProgress";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { Formik } from "formik";
import useMutation from "src/hooks/useMutation";
import { UPDATE_INVENTORY_REQUEST_MUTATION } from "src/constants/graphql/mutations";
import AdditionalChargesForm from "./AdditionalChargesForm";
import {
  getSectionsWithOneTimeCharges,
  buildInitialValues,
  validateCharges,
} from "src/components/capture/ReceivingWizard/AdditionalCharges/utils";
import { FETCH_PROJECT_QUERY } from "src/constants/graphql/queries";

export default function AdditionalChargesModal({
  inventoryRequestId,
  inventoryRequest,
  inventoryItems,
  onClose,
  currentItemIndex,
  onSubmit,
}) {
  const [updateInventoryRequest] = useMutation(UPDATE_INVENTORY_REQUEST_MUTATION);
  const totalItems = inventoryItems.length;
  const { data: { project = {} } = {}, loading } = useQuery(FETCH_PROJECT_QUERY, {
    variables: { id: inventoryRequest?.projectId },
  });
  const sections = useMemo(() => getSectionsWithOneTimeCharges(project, "outbound_enabled", inventoryRequest), [project, inventoryRequest]);
  const initialValues = useMemo(() => buildInitialValues(sections, inventoryRequest), [
    sections,
    inventoryRequest,
  ]);
  const validate = useCallback(values => validateCharges(values), []);

  const handleNoAdditionalCharges = useCallback(async () => {
    // Set no additional charges confirmation and auto-proceed
    const noChargesValues = {
      oneTimeCharges: { no_additional_charges: 0 },
      billingQuantities: inventoryRequest.billingQuantities,
    };

    try {
      await updateInventoryRequest({
        variables: {
          id: inventoryRequestId,
          input: noChargesValues,
        },
      });

      // Auto-proceed to next step
      onSubmit(noChargesValues);
    } catch (error) {
      console.error("Failed to save no ancillary charges:", error);
    }
  }, [updateInventoryRequest, inventoryRequest, inventoryRequestId, onSubmit]);

  const handleSubmit = useCallback(
    async (values, { setSubmitting }) => {
      const inputPayload = {};
      sections.forEach(sec => {
        const obj = {};
        sec.entries.forEach(([k]) => {
          const value = values?.[sec.key]?.[k];
          // Include all numeric values, including 0 (for real-world use cases)
          // Only exclude empty strings or undefined values
          if (value !== "" && value !== undefined && value !== null) {
            obj[k] = Number(value);
          }
        });
        // Include the section if it has any defined values
        if (Object.keys(obj).length > 0) {
          // Convert oneTimeCharges to list format for consistency
          if (sec.key === 'oneTimeCharges') {
            inputPayload[sec.key] = {
              list: Object.entries(obj).map(([notes, amount]) => ({ notes, amount }))
            };
          } else {
            inputPayload[sec.key] = obj;
          }
        }
      });

      const hasActualCharges = Object.keys(inputPayload).some(key => 
        ['materialCharges', 'laborCharges', 'equipmentCharges', 'openInspectCharges', 'oneTimeCharges'].includes(key)
      );

      // If there are actual charges and the inventory request previously had "no additional charges",
      // we need to clear the no_additional_charges flag

      if (hasActualCharges && inventoryRequest?.oneTimeCharges?.no_additional_charges !== undefined) {
        // Clear the entire oneTimeCharges if we're adding other charges
        // This removes the no_additional_charges flag
        if (!inputPayload.oneTimeCharges) {
          inputPayload.oneTimeCharges = { list: [] };
        }
      }

      // Preserve billingQuantities to prevent it from being cleared
      inputPayload.billingQuantities = inventoryRequest.billingQuantities;
      await updateInventoryRequest({
        variables: {
          id: inventoryRequestId,
          input: inputPayload,
        },
      });

      setSubmitting(false);
      onSubmit(inputPayload);
    },
    [sections, updateInventoryRequest, inventoryRequestId, inventoryRequest, onSubmit]
  );

  return (
    <Dialog open fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography fontSize={18} fontWeight={500} color="textPrimary">
            Ancillary Charges
          </Typography>
          {totalItems > 0 && (
            <Typography color="error" fontWeight={500}>
              {currentItemIndex} out of {totalItems} Items Picked
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <Divider />
      {loading ? (
        <CircularProgress />
      ) : (
        <Formik
          initialValues={initialValues}
          validate={validate}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          <AdditionalChargesForm
            inventoryRequest={inventoryRequest}
            project={project}
            currentItemIndex={currentItemIndex}
            totalItems={totalItems}
            onClose={onClose}
            onNoAdditionalCharges={handleNoAdditionalCharges}
          />
        </Formik>
      )}
    </Dialog>
  );
}
